/**
 * Standardized Education Level Options for EAB Connect
 * 
 * These education levels are designed to be:
 * - Internationally recognizable across different education systems
 * - Clear and understandable for both mentors and entrepreneurs
 * - Suitable for filtering, matching, and aggregate reporting
 */

export const EDUCATION_LEVEL_OPTIONS = [
  { value: "no_formal_education", label: "No Formal Education" },
  { value: "primary_elementary", label: "Primary / Elementary School" },
  { value: "lower_secondary", label: "Lower Secondary School" },
  { value: "upper_secondary", label: "Upper Secondary School (High School or Equivalent)" },
  { value: "vocational_technical", label: "Vocational / Technical Training" },
  { value: "some_college", label: "Some College / University (No Degree)" },
  { value: "associate_degree", label: "Associate Degree (or Equivalent)" },
  { value: "bachelors_degree", label: "Bachelor's Degree (BA, BS, or Equivalent)" },
  { value: "postgraduate_certificate", label: "Postgraduate Certificate / Diploma" },
  { value: "masters_degree", label: "Master's Degree (MA, MS, MBA, or Equivalent)" },
  { value: "doctoral_degree", label: "Doctoral Degree (PhD, EdD, or Equivalent)" },
  { value: "other", label: "Other" },
  { value: "prefer_not_to_say", label: "Prefer Not to Say" },
] as const;

/**
 * Labels for displaying education levels in analytics and reports
 * Maps stored values to human-readable labels
 */
export const EDUCATION_LEVEL_LABELS: Record<string, string> = {
  // New standardized values
  no_formal_education: "No Formal Education",
  primary_elementary: "Primary / Elementary School",
  lower_secondary: "Lower Secondary School",
  upper_secondary: "Upper Secondary School",
  vocational_technical: "Vocational / Technical Training",
  some_college: "Some College / University",
  associate_degree: "Associate Degree",
  bachelors_degree: "Bachelor's Degree",
  postgraduate_certificate: "Postgraduate Certificate / Diploma",
  masters_degree: "Master's Degree",
  doctoral_degree: "Doctoral Degree",
  other: "Other",
  prefer_not_to_say: "Prefer Not to Say",
  
  // Legacy value mappings (for backward compatibility with existing data)
  some_high_school: "Some High School (Legacy)",
  high_school_graduate: "High School Graduate (Legacy)",
  college: "College (Legacy)",
  trade_school: "Trade School (Legacy)",
};

/**
 * Maps legacy education values to their closest standardized equivalent
 * Used for data migration and display purposes
 */
export const LEGACY_EDUCATION_MAPPING: Record<string, string> = {
  some_high_school: "lower_secondary",
  high_school_graduate: "upper_secondary",
  college: "bachelors_degree",
  trade_school: "vocational_technical",
};

/**
 * Get the display label for an education level value
 * Handles both new and legacy values gracefully
 */
export function getEducationLabel(value: string | null | undefined): string {
  if (!value) return "Not Specified";
  return EDUCATION_LEVEL_LABELS[value] || value;
}

/**
 * Get the standardized value for legacy education entries
 * Returns the original value if no mapping exists
 */
export function getStandardizedEducationValue(value: string | null | undefined): string | null {
  if (!value) return null;
  return LEGACY_EDUCATION_MAPPING[value] || value;
}

/**
 * Helper text to display in the UI
 */
export const EDUCATION_HELPER_TEXT = "Education levels are shown in internationally recognized terms.";
